"use client"

import { useCallback } from "react"

import { analytics } from "@/lib/analytics/client"
import { createClient } from "@/lib/supabase/client"

import { sendMagicLink } from "@/app/actions/send-magic-link"

import { useLoginContext } from "./context"
import type { LoginControllerReturn } from "./types"

export function useLoginController(redirectTo: string): LoginControllerReturn {
  const {
    email,
    setIsLoading,
    setIsGoogleLoading,
    setIsMagicLinkSent,
    setError,
  } = useLoginContext()

  const supabase = createClient()

  const handleGoogleLogin = useCallback(async () => {
    setIsGoogleLoading(true)
    setError(null)

    analytics.capture("login_started", {
      method: "google_oauth",
      redirect_to: redirectTo,
    })

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectTo)}`,
      },
    })

    if (error) {
      setError("Erro ao conectar com Google. Tente novamente.")
      setIsGoogleLoading(false)
    }
  }, [supabase, redirectTo, setIsGoogleLoading, setError])

  const handleMagicLinkLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setIsLoading(true)
      setError(null)

      analytics.capture("magic_link_request_started", {
        redirect_to: redirectTo,
      })

      try {
        const result = await sendMagicLink(email, redirectTo)

        if (!result.success) {
          setError(result.error ?? "Erro ao enviar email. Tente novamente.")
        } else {
          setIsMagicLinkSent(true)
        }
      } catch {
        setError("Erro ao enviar email. Tente novamente.")
      } finally {
        setIsLoading(false)
      }
    },
    [email, redirectTo, setIsLoading, setError, setIsMagicLinkSent]
  )

  return {
    handleGoogleLogin,
    handleMagicLinkLogin,
  }
}
